/**
 * ¿Estamos viendo datos en vivo, o una foto congelada?  (FR-014, T043, T050)
 *
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  LA LIVENESS MIDE EL DATO, NO EL SOCKET.                                   ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * El peor fallo posible en esta herramienta: el sondeo muere, la API sigue viva,
 * el socket late feliz y el navegador dice "conectado" sobre una lista
 * congelada. Un operador miraría una conversación atascada y la creería en
 * marcha. Por eso hay DOS caídas distintas, y se distinguen a simple vista:
 *
 *   · `desconectado`  → el socket se cayó (la API no responde). Reconectando.
 *   · `sondeo-caido`  → el socket está VIVO pero el DATO está viejo: el sondeo
 *                       murió. Esto es lo que el socket solo NUNCA detectaría.
 *
 * Es función PURA a propósito: es lo que se prueba (T050).
 */

export type EstadoConexion = 'conectando' | 'en-vivo' | 'sondeo-caido' | 'desconectado'

export interface SnapshotConexion {
    /** El `EventSource` disparó `onerror` y aún no ha vuelto a abrir. */
    reconectando: boolean
    /** Reloj DEL NAVEGADOR (ms) del último latido recibido. `null` = ninguno aún. */
    ultimoLatidoRecibidoEn: number | null
    /** `poller_run.last_ok_at` del último latido (reloj del SERVIDOR). */
    lastOkAt: string | null
    /** `server_now` del último latido (reloj del SERVIDOR). */
    serverNow: string | null
}

/**
 * Sin latido en este tiempo (reloj del navegador) → el socket está encallado.
 * Es un cinturón por si `onerror` no salta; > 2 latidos (15 s) de margen.
 */
export const UMBRAL_SOCKET_MS = 40_000

/**
 * `server_now − last_ok_at` mayor que esto → el sondeo va viejo. Ambos son
 * relojes DEL SERVIDOR (sin desfase con el del navegador). En salud es de 2-4 s
 * (el sondeo corre cada 2 s); el umbral deja margen holgado.
 */
export const UMBRAL_SONDEO_MS = 10_000

export function clasificarConexion(s: SnapshotConexion, ahora: number): EstadoConexion {
    if (s.reconectando) return 'desconectado'
    if (s.ultimoLatidoRecibidoEn === null) return 'conectando'
    if (ahora - s.ultimoLatidoRecibidoEn > UMBRAL_SOCKET_MS) return 'desconectado'

    // Socket vivo. ¿Y el dato? Se comparan los DOS relojes del servidor entre sí
    // (no con el del navegador): mide la antigüedad real del sondeo, sin desfase.
    if (s.lastOkAt !== null && s.serverNow !== null) {
        const antiguedad = new Date(s.serverNow).getTime() - new Date(s.lastOkAt).getTime()
        if (antiguedad > UMBRAL_SONDEO_MS) return 'sondeo-caido'
    }
    return 'en-vivo'
}
